import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FormInput from '../common/FormInput.js';
import Button from '../common/Button.js';
import { authApi } from '../../services/authApi.js';
import useAppStore from '../../store/useAppStore.js';

const LoginPage = () => {
  const navigate = useNavigate();
  const login = useAppStore(state => state.login);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }
    
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const decodeJwt = (token) => {
    try {
      const payload = token.split('.')[1];
      let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const pad = base64.length % 4;
      if (pad === 2) base64 += '==';
      else if (pad === 3) base64 += '=';
      const decoded = atob(base64);
      return JSON.parse(decoded);
    } catch (error) {
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const data = await authApi.login(formData);

      // Store token
      localStorage.setItem('token', data.token);

      const tokenPayload = decodeJwt(data.token) || {};
      const user = {
        id: tokenPayload.id || null,
        role: tokenPayload.role || 'guest',
        name: data.user?.name || '',
        branch_id: tokenPayload.branch_id || null
      };

      // Store minimal user data
      localStorage.setItem('user', JSON.stringify(user));

      // Update store
      login(user, user.role, data.token);
      
      // Navigate using server-provided redirect path
      const redirectPath = data.redirectPath || '/';
      navigate(redirectPath);
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-pink-50 flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        backgroundImage: 'linear-gradient(135deg, rgba(255, 247, 237, 1) 0%, rgba(255, 255, 255, 1) 50%, rgba(252, 231, 243, 1) 100%)'
      }}
    >
      {/* Animated Overlay Background */}
      <div className="absolute inset-0 z-0">
        {/* Animated gradient orbs */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-r from-orange-300 to-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-r from-pink-300 to-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-gradient-to-r from-yellow-300 to-orange-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <style>{`
        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>

      <div className="w-full max-w-md relative z-10">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden backdrop-blur-sm">
          {/* Header Background */}
          <div
            className="h-24 bg-gradient-to-r from-orange-500 to-pink-500"
            style={{ backgroundImage: 'linear-gradient(90deg, #fb923c 0%, #ec4899 100%)' }}
          ></div>

          {/* Content */}
          <div className="px-8 py-8 relative">
            {/* Logo/Icon Area */}
            <div className="flex justify-center -mt-20 mb-6">
              <div
                className="w-24 h-24 rounded-full shadow-xl flex items-center justify-center border-4 border-white"
                style={{ backgroundImage: 'linear-gradient(135deg, #fbbf24 0%, #fb923c 100%)' }}
              >
                <i className="fa-solid fa-ice-cream text-6xl text-white"></i>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">Ice Cream World</h1>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <FormInput
                  id="email"
                  name="email"
                  label="Email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  error={errors.email}
                  icon="fa-solid fa-envelope"
                  required
                />
              </div>

              <div className="mb-4">
                <FormInput
                  id="password"
                  name="password"
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  error={errors.password}
                  icon="fa-solid fa-lock"
                  required
                />
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-gray-300" />
                  <span className="text-sm text-gray-600"><i className="fa-solid fa-check-circle text-green-500 mr-1"></i>Remember me</span>
                </label>
                <button type="button" className="text-sm text-orange-500 hover:text-orange-600 font-medium flex items-center gap-1 bg-none border-none p-0 cursor-pointer">
                  <i className="fa-solid fa-key"></i>Forgot password?
                </button>
              </div>

              {/* General Error */}
              {errors.general && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm flex items-center gap-2">
                    <i className="fa-solid fa-triangle-exclamation"></i>
                    {errors.general}
                  </p>
                </div>
              )}

              {/* Login Button */}
              <Button
                type="submit"
                variant="primary"
                fullWidth
                loading={loading}
                icon={<i className="fa-solid fa-right-to-bracket"></i>}
              >
                Login
              </Button>
            </form>
          </div>
        </div>

        {/* Additional Info */}
         <p className="text-center text-gray-500 text-xs mt-6">
          Developed by <a href="https://usama4.vercel.app/" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-600">Usama Khan</a>.
        </p>
        <p className="text-center text-gray-500 text-xs mt-2">
          © 2026 Ice Cream World. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
